import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { LoginPage } from './pages/Login';
import { RequireAuth } from './auth/RequireAuth';
import { PageSkeleton } from './components/PageSkeleton';

const DashboardPage = lazy(() => import('./pages/Dashboard').then(m => ({ default: m.DashboardPage })));
const MyWorkPage = lazy(() => import('./pages/MyWork'));
const ProjectsPage = lazy(() => import('./pages/Projects').then(m => ({ default: m.ProjectsPage })));
const GanttPage = lazy(() => import('./pages/Gantt').then(m => ({ default: m.GanttPage })));
const TimesheetPage = lazy(() => import('./pages/Timesheet').then(m => ({ default: m.TimesheetPage })));
const ApprovalsPage = lazy(() => import('./pages/Approvals').then(m => ({ default: m.ApprovalsPage })));
const ReportsPage = lazy(() => import('./pages/Reports').then(m => ({ default: m.ReportsPage })));
const ImportPage = lazy(() => import('./pages/Import').then(m => ({ default: m.ImportPage })));
const Notifications = lazy(() => import('./pages/Notifications'));
const Kanban = lazy(() => import('./pages/Kanban'));
const Calendar = lazy(() => import('./pages/Calendar'));
const Search = lazy(() => import('./pages/Search'));
const AuditTrail = lazy(() => import('./pages/AuditTrail'));
const Resources = lazy(() => import('./pages/Resources'));
const ProjectDetailPage = lazy(() => import('./pages/ProjectDetail'));
const PortfoliosPage = lazy(() => import('./pages/Portfolios'));
const ProgramsPage = lazy(() => import('./pages/Programs'));
const RaidBoardPage = lazy(() => import('./pages/RaidBoard'));
const BlueprintsPage = lazy(() => import('./pages/Blueprints'));
const IntakePage = lazy(() => import('./pages/Intake'));
const WorkloadPage = lazy(() => import('./pages/Workload'));
const SettingsPage = lazy(() => import('./pages/Settings'));
const ScorecardsPage = lazy(() => import('./pages/Scorecards'));
const NewProjectPage = lazy(() => import('./pages/NewProject'));
const IntakePublicPage = lazy(() => import('./pages/IntakePublic'));
const AcceptInvitePage = lazy(() => import('./pages/AcceptInvite'));

function R({ children }: { children: React.ReactNode }) {
  return <RequireAuth>{children}</RequireAuth>;
}

export function App() {
  return (
    <Suspense fallback={<PageSkeleton />}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/accept-invite" element={<AcceptInvitePage />} />
        <Route path="/intake/:tenantSlug/:slug" element={<IntakePublicPage />} />
        <Route path="/dashboard"           element={<R><DashboardPage /></R>} />
        <Route path="/my-work"             element={<R><MyWorkPage /></R>} />
        <Route path="/projects"            element={<R><ProjectsPage /></R>} />
        <Route path="/projects/new"              element={<R><NewProjectPage /></R>} />
        <Route path="/projects/:id"              element={<R><ProjectDetailPage /></R>} />
        <Route path="/projects/:projectId/gantt" element={<R><GanttPage /></R>} />
        <Route path="/timesheet"           element={<R><TimesheetPage /></R>} />
        <Route path="/approvals"           element={<R><ApprovalsPage /></R>} />
        <Route path="/reports"             element={<R><ReportsPage /></R>} />
        <Route path="/import"              element={<R><ImportPage /></R>} />
        <Route path="/notifications"       element={<R><Notifications /></R>} />
        <Route path="/kanban"              element={<R><Kanban /></R>} />
        <Route path="/calendar"            element={<R><Calendar /></R>} />
        <Route path="/search"              element={<R><Search /></R>} />
        <Route path="/audit"               element={<R><AuditTrail /></R>} />
        <Route path="/resources"           element={<R><Resources /></R>} />
        <Route path="/portfolios"          element={<R><PortfoliosPage /></R>} />
        <Route path="/portfolios/:id"      element={<R><PortfoliosPage /></R>} />
        <Route path="/programs"            element={<R><ProgramsPage /></R>} />
        <Route path="/programs/:id"        element={<R><ProgramsPage /></R>} />
        <Route path="/raid"                element={<R><RaidBoardPage /></R>} />
        <Route path="/blueprints"          element={<R><BlueprintsPage /></R>} />
        <Route path="/intake"              element={<R><IntakePage /></R>} />
        <Route path="/workload"            element={<R><WorkloadPage /></R>} />
        <Route path="/settings"            element={<R><SettingsPage /></R>} />
        <Route path="/scorecards"          element={<R><ScorecardsPage /></R>} />
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Suspense>
  );
}
